import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { StaticPropsComponentExtensions } from '@shared/feature/component-static-extensions';
import { VerificationCodeRequest, RegisterGuestRequest } from '../../models/request.models';
import { NotificationService } from '@shared/feature/notification-handling';
import { BaseModalComponent } from '@shared/ui/modal';
import { ActivatedRoute } from '@angular/router';
import { PageLayoutApiService } from '../../services/page-layout/page-layout-api.service';
import { UrlNavigationService } from '@shared/utils/url-navigation';
import { Subject, takeUntil } from 'rxjs';
import { TranslationFromJsonService } from '@shared/feature/translate';

@Component({
  selector: 'feature-page-layout-verify-phone-number',
  templateUrl: './verify-phone-number.component.html',
  styleUrls: ['./verify-phone-number.component.css'],
  standalone: false,
})
export class VerifyPhoneNumberComponent extends StaticPropsComponentExtensions implements OnInit, OnDestroy {
  @ViewChild(BaseModalComponent) modal?: BaseModalComponent;

  formGroup = new FormGroup({
    code: new FormControl('', { validators: [Validators.required, Validators.minLength(6), Validators.maxLength(6)] }),
  });

  request!: RegisterGuestRequest;
  phoneNumber = '';
  userUid = '';
  caseUid: string | null = null;
  fileUid: string | null = null;
  disableButtons = false;

  private ngUnsubscribe$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private verificationService: PageLayoutApiService,
    private notificationService: NotificationService,
    private urlNavigationService: UrlNavigationService,
    private translationService: TranslationFromJsonService
  ) {
    super();
  }

  ngOnInit(): void {
    this.fileUid = this.route.snapshot.queryParamMap.get('fileUid');
    this.caseUid = this.route.snapshot.queryParamMap.get('caseUid');
  }

  resendCode(): void {
    this.disableButtons = true;
    this.verificationService
      .sendVerificationRequest(this.userUid, new VerificationCodeRequest(this.phoneNumber))
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: () => {
          this.notificationService.addSuccess(this.translationService.instant('successfully-performed-operation'));
          this.disableButtons = false;
        },
        error: () => {
          this.notificationService.addError(this.translationService.instant('something-went-wrong'));
          this.disableButtons = false;
        },
      });
  }

  completeRegistration(): void {
    const code = this.formGroup.get('code')?.value;
    if (code) {
      this.request = { ...this.request, VerificationCode: code };

      this.disableButtons = true;

      this.verificationService
        .registerGuest(this.userUid, this.request)
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe({
          next: () => {
            if (this.modal) {
              this.modal.activeModal.close();
              this.urlNavigationService.openInSameTab(`http://localhost:40000/login?returnUrl=http://localhost:4200/${this.getReturnUrl()}`);
            }
          },
          error: (error) => {
            if (error.error && error.error['Message']) {
              this.notificationService.addError(error.error['Message']);
            } else {
              this.notificationService.addError(this.translationService.instant('something-went-wrong'));
            }

            this.disableButtons = false;
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private getReturnUrl(): string {
    if (this.caseUid && this.caseUid.length > 0) {
      return `cases/${this.caseUid}`;
    } else if (this.fileUid && this.fileUid.length > 0) {
      return `files/${this.fileUid}?isFullScreen=True`;
    }

    return `files?pageType=1`;
  }
}
